import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import type { BoundHcoResponse } from './interface/bound-hco-response';
import type { CreateOrderRequest, CreateOrderResponse } from './interface/create-order-request';
import type { HcpProfileResponse } from './interface/hcp-profile-response';
import type { LatestOrderResponse } from './interface/latest-order-response';
import type { OrderHistoryResponse } from './interface/order-history-response';
import type { OrderHistoryItem } from './interface/order-history-response';
import type { OrderEntryAccessResponse } from './interface/order-entry-access-response';
import type {
  ProductAvailabilityResponse,
  ProductCatalogResponse,
  ProductTypeListResponse,
} from './interface/product-catalog-response';
import type { SalesHcpSearchResponse } from './interface/sales-hcp-search-response';
import type { SalesOrderPermissionResponse } from './interface/sales-order-permission-response';
import type { SalesProfileResponse } from './interface/sales-profile-response';
import type { ValidatedOrdersIdentity } from './interface/validated-orders-identity';

@Injectable({
  providedIn: 'root',
})
export class OrdersAccessService {
  /** 快取入口驗證結果，避免同一使用者在單次流程內重複查詢。 */
  private validatedIdentityCache: ValidatedOrdersIdentity | null = null;
  private validatedIdentityCacheKey = '';
  /** 快取最近訂單資料，避免 recent-order 頁面重進時重複打 API。 */
  private latestHcpOrderCache: LatestOrderResponse['data'] | undefined = undefined;
  private latestHcpOrderCacheKey = '';

  constructor(private http: HttpClient) {}

  /**
   * 先打共用入口 API，再依 identityType 做第二層身份主資料驗證。
   */
  async validateOrdersIdentity(uid: string, token: string): Promise<ValidatedOrdersIdentity> {
    const nextCacheKey = `${uid}:${token}`;

    if (this.validatedIdentityCache && this.validatedIdentityCacheKey === nextCacheKey) {
      return this.validatedIdentityCache;
    }

    let access: OrderEntryAccessResponse;

    try {
      access = await this.checkOrdersEntryAccess(uid, token);
    } catch (error) {
      console.error('checkOrdersEntryAccess failed, fallback to blocked page.', error);
      const blockedIdentity: ValidatedOrdersIdentity = {
        // 第一層入口查詢失敗時，identityType 已不可得；此處只需回傳一個合法預設值，
        // 讓 guard 依 entryEnabled=false 一律導到 blocked。
        identityType: 'hcp',
        refId: '',
        entryEnabled: false,
      };
      this.validatedIdentityCache = blockedIdentity;
      this.validatedIdentityCacheKey = nextCacheKey;
      return blockedIdentity;
    }

    if (!access.enable) {
      const disabledIdentity: ValidatedOrdersIdentity = {
        identityType: access.identityType,
        refId: access.refId,
        entryEnabled: false,
      };
      this.validatedIdentityCache = disabledIdentity;
      this.validatedIdentityCacheKey = nextCacheKey;
      return disabledIdentity;
    }

    const validatedIdentity =
      access.identityType === 'hcp'
        ? {
            identityType: access.identityType,
            refId: access.refId,
            entryEnabled: true,
            hcpProfile: await this.getHcpProfileByUid(uid, token),
          }
        : {
            identityType: access.identityType,
            refId: access.refId,
            entryEnabled: true,
            salesProfile: await this.getSalesProfileByUid(uid, token),
          };

    this.validatedIdentityCache = validatedIdentity;
    this.validatedIdentityCacheKey = nextCacheKey;

    return validatedIdentity;
  }

  /**
   * 取得 HCP 最新一筆訂單。
   *
   * 若 API 回傳 404，表示此 HCP 尚未建立任何訂單，前端應視為「沒有最近訂單」。
   */
  async getLatestHcpOrder(uid: string, token: string): Promise<LatestOrderResponse['data']> {
    const nextCacheKey = `${uid}:${token}`;

    if (this.latestHcpOrderCacheKey === nextCacheKey && this.latestHcpOrderCache !== undefined) {
      return this.latestHcpOrderCache;
    }

    const latestOrderResponse = await firstValueFrom(
      this.http.get<LatestOrderResponse>(`${this.getAwsApiBaseUrl()}/orders/by-hcp-uid/${uid}/latest`, {
        headers: this.buildAuthHeaders(token),
      })
    );

    this.latestHcpOrderCache = latestOrderResponse.data;
    this.latestHcpOrderCacheKey = nextCacheKey;
    return latestOrderResponse.data;
  }

  /**
   * 依 HCP UID 取得歷史訂單列表。
   *
   * 此 API 採後端分頁，前端需自行帶入 `page` 與 `pageSize`，
   * 以支援首屏載入與 infinite scroll 的續頁抓取。
   */
  async getOrdersByHcpUid(
    hcpUid: string,
    page: number,
    pageSize: number,
    token: string
  ): Promise<OrderHistoryResponse> {
    return await firstValueFrom(
      this.http.get<OrderHistoryResponse>(`${this.getAwsApiBaseUrl()}/orders`, {
        headers: this.buildAuthHeaders(token),
        params: {
          hcpUid,
          page,
          pageSize,
        },
      })
    );
  }

  /**
   * 取得 HCP 累計有效訂單數。
   *
   * 初版會員頁只需要總筆數，因此沿用歷史訂單分頁 API 並以最小 pageSize 讀取 `total`。
   */
  async getHcpOrderCount(hcpUid: string, token: string): Promise<number> {
    const response = await this.getOrdersByHcpUid(hcpUid, 1, 1, token);

    return response.total;
  }

  /**
   * 依訂單 id 取得單筆訂單明細。
   */
  async getOrderById(orderId: string, token: string): Promise<OrderHistoryItem> {
    return await firstValueFrom(
      this.http.get<OrderHistoryItem>(`${this.getAwsApiBaseUrl()}/orders/${orderId}`, {
        headers: this.buildAuthHeaders(token),
      })
    );
  }

  /**
   * 取得前端產品分類選單資料。
   */
  async getProductTypes(token: string): Promise<ProductTypeListResponse['data']> {
    const productTypesResponse = await firstValueFrom(
      this.http.get<ProductTypeListResponse>(`${this.getAwsApiBaseUrl()}/product-types`, {
        headers: this.buildAuthHeaders(token),
      })
    );

    return productTypesResponse.data;
  }

  /**
   * 判斷系統是否至少有一筆前台可訂購產品。
   */
  async hasEnabledProducts(token: string): Promise<boolean> {
    const response = await firstValueFrom(
      this.http.get<ProductAvailabilityResponse>(`${this.getAwsApiBaseUrl()}/products/availability`, {
        headers: this.buildAuthHeaders(token),
      })
    );

    return response.hasEnabledProducts;
  }

  /**
   * 依指定院所取得可顯示的產品清單與促銷方案權限。
   */
  async getProductsByHcoId(hcoId: string, token: string): Promise<ProductCatalogResponse['data']> {
    const productsResponse = await firstValueFrom(
      this.http.get<ProductCatalogResponse>(`${this.getAwsApiBaseUrl()}/products`, {
        headers: this.buildAuthHeaders(token),
        params: {
          hcoId,
        },
      })
    );

    return productsResponse.data;
  }

  /**
   * 取得指定 HCP 綁定的院所清單，供前端回推某筆訂單對應的 `hcoId`。
   */
  async getBoundHcosByHcpUid(uid: string, token: string): Promise<BoundHcoResponse['data']> {
    const boundHcosResponse = await firstValueFrom(
      this.http.get<BoundHcoResponse>(`${this.getAwsApiBaseUrl()}/hcps/hcos`, {
        headers: this.buildAuthHeaders(token),
        params: {
          hcpUid: uid,
        },
      })
    );

    return boundHcosResponse.data;
  }

  /**
   * 判斷指定院所是否仍在 HCP 目前的綁定院所清單中。
   */
  async isHcoBoundToHcp(uid: string, hcoId: string, token: string): Promise<boolean> {
    const normalizedHcoId = hcoId.trim();

    if (!normalizedHcoId) {
      return false;
    }

    const boundHcos = await this.getBoundHcosByHcpUid(uid, token);

    return boundHcos.some((hco) => hco._id === normalizedHcoId);
  }

  /**
   * 業務以院所名稱或 HCP 姓名查詢可代訂的 HCP 清單。
   */
  async searchSalesHcps(
    salesUid: string,
    token: string,
    filters: {
      hcoName?: string;
      hcpName?: string;
    }
  ): Promise<SalesHcpSearchResponse['data']> {
    const hcoName = filters.hcoName?.trim() || '';
    const hcpName = filters.hcpName?.trim() || '';
    const response = await firstValueFrom(
      this.http.get<SalesHcpSearchResponse>(`${this.getAwsApiBaseUrl()}/sales/hcps`, {
        headers: this.buildAuthHeaders(token),
        params: {
          salesUid,
          ...(hcoName ? { hcoName } : {}),
          ...(hcpName ? { hcpName } : {}),
        },
      })
    );

    return response.data;
  }

  /**
   * 驗證業務是否可替指定 HCP 代訂。
   */
  async getSalesOrderPermission(salesUid: string, hcpId: string, token: string): Promise<SalesOrderPermissionResponse> {
    return await firstValueFrom(
      this.http.get<SalesOrderPermissionResponse>(`${this.getAwsApiBaseUrl()}/sales/order-permission`, {
        headers: this.buildAuthHeaders(token),
        params: {
          salesUid,
          hcpId,
        },
      })
    );
  }

  /**
   * 建立訂單。
   */
  async submitOrder(payload: CreateOrderRequest, token: string): Promise<CreateOrderResponse> {
    return await firstValueFrom(
      this.http.post<CreateOrderResponse>(`${this.getAwsApiBaseUrl()}/orders`, payload, {
        headers: this.buildAuthHeaders(token),
      })
    );
  }

  /**
   * 建立訂單成功後送出 LINE 推播通知。
   */
  async pushOrderCreated(lineUserId: string, orderId: string, token: string) {
    return await firstValueFrom(
      this.http.post(
        `${this.getAwsApiBaseUrl()}/orders/push`,
        {
          lineUserId,
          orderId,
        },
        {
          headers: this.buildAuthHeaders(token),
        }
      )
    );
  }

  /**
   * 清除目前登入使用者的入口驗證快取。
   *
   * 當 token、LINE uid 或身份資料可能已更新時，可主動呼叫此方法避免沿用舊結果。
   */
  clearValidatedIdentityCache() {
    this.validatedIdentityCache = null;
    this.validatedIdentityCacheKey = '';
    this.latestHcpOrderCache = undefined;
    this.latestHcpOrderCacheKey = '';
  }

  /**
   * 呼叫第一層共用入口 API。
   *
   * 後端會依 LINE uid 回傳 `identityType`、`refId` 與 `enable`，
   * 供前端決定是否要繼續做第二層身份驗證。
   */
  private async checkOrdersEntryAccess(uid: string, token: string): Promise<OrderEntryAccessResponse> {
    // 共用入口 API 先判斷此 LINE 使用者是否具備進入 orders domain 的資格。
    return await firstValueFrom(
      this.http.get<OrderEntryAccessResponse>(`${this.getAwsApiBaseUrl()}/user-identities/${uid}`, {
        headers: this.buildAuthHeaders(token),
      })
    );
  }

  /**
   * 呼叫 HCP 第二層主資料 API。
   *
   * 入口 guard 只需要確認 HCP 主資料存在，並判斷 `canOrder` 是否為 true。
   */
  private async getHcpProfileByUid(uid: string, token: string): Promise<HcpProfileResponse> {
    // HCP 第二層 API 只回最小主資料，避免前端入口驗證過度拿個資。
    return await firstValueFrom(
      this.http.get<HcpProfileResponse>(`${this.getAwsApiBaseUrl()}/hcps/by-uid/${uid}`, {
        headers: this.buildAuthHeaders(token),
      })
    );
  }

  /**
   * 呼叫 medSales 第二層主資料 API。
   *
   * 入口 guard 只需要確認資料存在，並判斷業務身份是否仍為啟用狀態。
   */
  private async getSalesProfileByUid(uid: string, token: string): Promise<SalesProfileResponse> {
    // medSales 第二層 API 只回入口驗證所需欄位，不展開 territory 等內部權限資料。
    return await firstValueFrom(
      this.http.get<SalesProfileResponse>(`${this.getAwsApiBaseUrl()}/sales/by-uid/${uid}`, {
        headers: this.buildAuthHeaders(token),
      })
    );
  }

  /**
   * 取得 AWS API base URL。
   *
   * 若環境設定缺漏，提早在 service 層拋錯，避免發出錯誤的 HTTP request。
   */
  private getAwsApiBaseUrl() {
    if (!environment.aws.api) {
      throw new Error('AWS API base URL is not configured.');
    }

    return environment.aws.api;
  }

  /**
   * 建立 AWS API 共用的 Bearer token header。
   *
   * 所有第二層驗證 API 都依賴 Firebase custom token，因此在這裡集中檢查與組裝。
   */
  private buildAuthHeaders(token: string) {
    if (!token) {
      throw new Error('Custom token is empty.');
    }

    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });
  }
}
